import { useEffect, useState, useCallback } from "react";
import type { MomentImageLibraryItem, MomentImageLibraryPage } from "../api/momentApi";
import { listMomentImages } from "../api/momentApi";
import type { AllowedSize } from "../utils/imageValidator";
import { validateImageDimension, formatAllowedSizes } from "../utils/imageValidator";

type Props = {
  open: boolean;
  allowedSizes: AllowedSize[];
  onSelect: (item: MomentImageLibraryItem) => void;
  onClose: () => void;
};

export default function MomentImageLibraryModal({ open, allowedSizes, onSelect, onClose }: Props) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MomentImageLibraryPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  const load = useCallback((p: number) => {
    setLoading(true);
    setFetchError(null);
    listMomentImages({ page: p, size: 9, sizes: allowedSizes })
      .then((result) => { setData(result); setPage(p); })
      .catch(() => setFetchError("이미지 목록을 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [allowedSizes]);

  useEffect(() => {
    if (open) { load(1); setSelectError(null); }
  }, [open, load]);

  const handleSelect = async (item: MomentImageLibraryItem) => {
    setSelectError(null);
    setValidating(true);
    try {
      if (item.imgWidth !== null && item.imgHeight !== null) {
        const matched = allowedSizes.some(([w, h]) => item.imgWidth === w && item.imgHeight === h);
        if (!matched) {
          setSelectError(`이미지 크기는 ${formatAllowedSizes(allowedSizes)}여야 합니다. (현재: ${item.imgWidth}×${item.imgHeight}px)`);
          return;
        }
      } else {
        const err = await validateImageDimension(item.imgUrl, allowedSizes);
        if (err) { setSelectError(err); return; }
      }
      onSelect(item);
    } finally {
      setValidating(false);
    }
  };

  if (!open) return null;

  const totalPages = data ? data.totalPages : 0;

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-flex tw-flex-col">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
          <div>
            <h3 className="tw-font-bold tw-text-base tw-text-gray-800">이미지 라이브러리</h3>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">권장 사이즈: {formatAllowedSizes(allowedSizes)}</p>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => e.key === "Enter" && onClose()}
            className="tw-text-gray-300 hover:tw-text-gray-500 tw-text-2xl tw-leading-none tw-cursor-pointer tw-select-none tw-transition-colors tw-px-1"
          >
            &times;
          </div>
        </div>

        <div className="tw-p-6">
          {selectError && (
            <div className="tw-mb-4 tw-text-xs tw-text-red-600 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-px-3 tw-py-2">
              {selectError}
            </div>
          )}
          {loading && (
            <div className="tw-text-center tw-text-sm tw-text-gray-400 tw-py-16">로딩 중...</div>
          )}
          {fetchError && (
            <div className="tw-text-center tw-text-sm tw-text-red-500 tw-py-16">{fetchError}</div>
          )}
          {!loading && !fetchError && data && (() => {
            return data.content.length === 0 ? (
              <div className="tw-text-center tw-text-sm tw-text-gray-400 tw-py-16">
                사용 가능한 이미지가 없습니다.
              </div>
            ) : (
              <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                {data.content.map((item) => (
                  <div
                    key={item.uid}
                    role="button"
                    tabIndex={0}
                    onClick={() => !validating && handleSelect(item)}
                    onKeyDown={(e) => e.key === "Enter" && !validating && handleSelect(item)}
                    className={`tw-relative tw-group tw-aspect-square tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-select-none tw-transition-colors ${
                      validating ? "tw-opacity-50 tw-cursor-default" : ""
                    }`}
                    style={{ border: "1px solid #e5e7eb" }}
                  >
                    <img
                      src={item.imgUrl}
                      alt={item.imgName}
                      className="tw-w-full tw-h-full tw-object-cover"
                    />
                    <div className="tw-absolute tw-inset-0 tw-bg-black/0 group-hover:tw-bg-black/20 tw-transition-colors tw-flex tw-items-end">
                      <div className="tw-w-full tw-bg-black/60 tw-px-2 tw-py-1 tw-text-white tw-text-xs tw-truncate tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                        {item.imgName}
                        <span className="tw-ml-1 tw-text-gray-300">{item.imgWidth}×{item.imgHeight}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {data && (
          <div className="tw-flex tw-justify-center tw-items-center tw-gap-3 tw-px-6 tw-py-3 tw-border-t tw-border-gray-200">
            <div
              role="button"
              tabIndex={0}
              onClick={() => page > 1 && !loading && load(page - 1)}
              onKeyDown={(e) => e.key === "Enter" && page > 1 && !loading && load(page - 1)}
              className={`tw-text-xs tw-px-3 tw-py-1.5 tw-rounded-lg tw-cursor-pointer tw-select-none tw-transition-colors ${
                page === 1 || loading
                  ? "tw-text-gray-300 tw-bg-gray-50 tw-cursor-default"
                  : "tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200"
              }`}
            >
              이전
            </div>
            <span className="tw-text-xs tw-text-gray-500 tw-min-w-[60px] tw-text-center">
              {page} / {totalPages || 1}
            </span>
            <div
              role="button"
              tabIndex={0}
              onClick={() => page < totalPages && !loading && load(page + 1)}
              onKeyDown={(e) => e.key === "Enter" && page < totalPages && !loading && load(page + 1)}
              className={`tw-text-xs tw-px-3 tw-py-1.5 tw-rounded-lg tw-cursor-pointer tw-select-none tw-transition-colors ${
                page === totalPages || loading
                  ? "tw-text-gray-300 tw-bg-gray-50 tw-cursor-default"
                  : "tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200"
              }`}
            >
              다음
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
